/**
 * Curated First from Source
 *
 * Pins a product to position #1 in a collection grid via URL parameters.
 * Designed for Facebook/Meta ad landing pages.
 *
 * URL params:
 *   first   - Product handle or full encoded product URL
 *   variant - Variant ID to deep-link (optional, also extracted from encoded URL)
 *
 * Examples:
 *   /collections/best-sellers?first=my-product&variant=12345
 *   /collections/best-sellers?first=https%3A%2F%2Fshop.com%2Fproducts%2Fmy-product%3Fvariant%3D12345
 */
(function () {
  'use strict';

  function init() {
    var params = new URLSearchParams(window.location.search);
    var firstParam = params.get('first');
    if (!firstParam) return;

    var variantParam = params.get('variant');
    var parsed = parseFirstParam(firstParam, variantParam);

    var grid = document.querySelector('.filters-adjacent .product-grid');
    if (!grid) {
      grid = document.querySelector('.product-grid');
    }
    if (!grid) return;

    var existingCard = findCardByHandle(grid, parsed.handle);

    if (existingCard) {
      pinCard(grid, existingCard, parsed.variant, parsed.handle);
    } else {
      fetchAndPinProduct(grid, parsed.handle, parsed.variant);
    }
  }

  /**
   * Parse the `first` param — could be a plain handle or a full/encoded URL.
   * Also extracts variant from the encoded URL if not provided separately.
   */
  function parseFirstParam(firstParam, fallbackVariant) {
    var handle = firstParam;
    var variant = fallbackVariant || null;

    try {
      var decoded = decodeURIComponent(firstParam);
      if (decoded.indexOf('http') === 0) {
        var url = new URL(decoded);
        var parts = url.pathname.split('/');
        var idx = parts.indexOf('products');
        if (idx !== -1 && parts[idx + 1]) {
          handle = parts[idx + 1];
        }
        if (!variant) {
          var innerParams = new URLSearchParams(url.search);
          variant = innerParams.get('variant');
        }
      }
    } catch (e) {
      // Not a valid URL — treat as a plain handle
    }

    // Strip query strings or fragments from handle
    handle = handle.split('?')[0].split('#')[0];

    return { handle: handle, variant: variant };
  }

  /**
   * Find a product-block in the grid whose link matches the given handle.
   */
  function findCardByHandle(grid, handle) {
    var cards = grid.querySelectorAll('product-block');
    var pattern = '/products/' + handle;
    for (var i = 0; i < cards.length; i++) {
      var links = cards[i].querySelectorAll('a.product-link');
      for (var j = 0; j < links.length; j++) {
        if (links[j].getAttribute('href') && links[j].getAttribute('href').indexOf(pattern) !== -1) {
          return cards[i];
        }
      }
    }
    return null;
  }

  /**
   * Move a card to the first position in the grid and apply variant deep-link.
   */
  function pinCard(grid, card, variantId, handle) {
    if (grid.firstElementChild !== card) {
      grid.insertBefore(card, grid.firstElementChild);
    }
    if (variantId) {
      setVariantOnLinks(card, variantId);
      swapVariantImage(card, handle, variantId);
    }
  }

  /**
   * Fetch a product card via Section Rendering API and insert at position #1.
   */
  function fetchAndPinProduct(grid, handle, variantId) {
    var url = '/products/' + handle + '?section_id=curated-product-card';
    fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error('Product not found');
        return res.text();
      })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var card = doc.querySelector('product-block');
        if (card) {
          if (variantId) {
            setVariantOnLinks(card, variantId);
          }
          grid.insertBefore(card, grid.firstElementChild);
          if (variantId) {
            swapVariantImage(card, handle, variantId);
          }
        }
      })
      .catch(function (err) {
        console.warn('[curated-first] Could not load pinned product:', err.message);
      });
  }

  /**
   * Fetch product JSON and swap the card's image to the variant's featured image.
   */
  function swapVariantImage(card, handle, variantId) {
    fetch('/products/' + handle + '.json')
      .then(function (res) {
        if (!res.ok) throw new Error('Could not fetch product JSON');
        return res.json();
      })
      .then(function (data) {
        var product = data.product;
        var variantIdNum = parseInt(variantId, 10);
        var variant = null;
        for (var i = 0; i < product.variants.length; i++) {
          if (product.variants[i].id === variantIdNum) {
            variant = product.variants[i];
            break;
          }
        }
        if (!variant) return;

        // Try featured_image first, then fallback to image_id lookup
        if (variant.featured_image) {
          swapImage(card, variant.featured_image.src);
        } else if (variant.image_id) {
          for (var j = 0; j < product.images.length; j++) {
            if (product.images[j].id === variant.image_id) {
              swapImage(card, product.images[j].src);
              return;
            }
          }
        }
      })
      .catch(function () {
        // Silently fail — card still works, just shows default image
      });
  }

  /**
   * Replace all product card images with the variant image.
   */
  function swapImage(card, imageSrc) {
    var baseSrc = imageSrc.replace(/\?v=\d+/, '');

    var imageDivs = card.querySelectorAll('.product-block__image');
    imageDivs.forEach(function (div) {
      var img = div.querySelector('img');
      if (!img) return;

      img.setAttribute('src', resizeSrc(baseSrc, 800));

      // Build new srcset from scratch
      // (Shopify uses protocol-relative //cdn URLs that break regex replacement)
      var widths = [340, 480, 740, 980, 1200, 1400, 1600];
      var newSrcset = widths.map(function (w) {
        return resizeSrc(baseSrc, w) + ' ' + w + 'w';
      }).join(', ');
      img.setAttribute('srcset', newSrcset);
    });
  }

  /**
   * Build a Shopify CDN URL at a given width.
   */
  function resizeSrc(baseSrc, width) {
    var separator = baseSrc.indexOf('?') !== -1 ? '&' : '?';
    return baseSrc + separator + 'width=' + width;
  }

  /**
   * Append ?variant=ID to all product links inside a card.
   */
  function setVariantOnLinks(card, variantId) {
    var links = card.querySelectorAll('a[href*="/products/"]');
    for (var i = 0; i < links.length; i++) {
      var href = links[i].getAttribute('href');
      if (!href) continue;
      if (href.indexOf('variant=') !== -1) continue;
      var separator = href.indexOf('?') !== -1 ? '&' : '?';
      links[i].setAttribute('href', href + separator + 'variant=' + variantId);
    }
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
